import {
  BRIDGE_SEATS,
  type BridgeSeat,
  cardId,
  heartsViewFor,
  sortHand,
} from '@strategy4kids/game-engine';
import { PlayingCard, CARD_SLOT_CLASS, HAND_CLASS } from '../components/PlayingCard.js';
import { CardTable } from '../components/table/CardTable.js';
import { RoundSummaryPanel, type RoundSummary } from '../components/table/RoundSummaryPanel.js';
import { ScoreStrip } from '../components/table/ScoreStrip.js';
import { SeatPlate } from '../components/table/SeatPlate.js';
import { TrickArea } from '../components/table/TrickArea.js';
import { usePlayerLookStore } from '../state/player-look-store.js';
import { useSeatPersonas } from '../state/seat-personas.js';
import { useT } from '../i18n/useT.js';
import { HUMAN_SEAT, useHeartsStore } from '../state/hearts-store.js';
import { useSeatBubbles } from '../state/table-chatter.js';

/**
 * The Hearts screen: you in South, robots in the other seats, every player for themselves. It renders only from
 * `heartsViewFor(game, HUMAN_SEAT)`, so a hand you may not see is never in the data this component can reach.
 * The goal is to take as few points as possible: each heart is 1 and the queen of spades is 13.
 */
export function HeartsScreen({ onExit }: { onExit: () => void }) {
  const { t } = useT('games');
  const game = useHeartsStore((s) => s.game);
  const result = useHeartsStore((s) => s.result);
  const totals = useHeartsStore((s) => s.totals);
  const target = useHeartsStore((s) => s.target);
  const gameWinner = useHeartsStore((s) => s.gameWinner);
  const handNumber = useHeartsStore((s) => s.handNumber);
  const seed = useHeartsStore((s) => s.seed);
  const strength = useHeartsStore((s) => s.strength);
  const fourColour = useHeartsStore((s) => s.fourColour);
  const selected = useHeartsStore((s) => s.selected);
  const togglePass = useHeartsStore((s) => s.togglePass);
  const confirmPass = useHeartsStore((s) => s.confirmPass);
  const play = useHeartsStore((s) => s.play);
  const nextHand = useHeartsStore((s) => s.nextHand);
  const newGame = useHeartsStore((s) => s.newGame);
  const setStrength = useHeartsStore((s) => s.setStrength);
  const setFourColour = useHeartsStore((s) => s.setFourColour);
  const bigCards = useHeartsStore((s) => s.bigCards);
  const setBigCards = useHeartsStore((s) => s.setBigCards);

  const look = usePlayerLookStore();
  const personas = useSeatPersonas(BRIDGE_SEATS);
  const bubbles = useSeatBubbles();
  if (!game) return null;
  const view = heartsViewFor(game, HUMAN_SEAT);
  const seatName = (s: BridgeSeat) => t(`cardgame-seat-${s}`);
  const legal = new Set(view.legalPlays.map(cardId));
  const chosen = new Set(selected.map(cardId));
  const received = new Set(view.received.map(cardId));
  const passing = view.phase === 'pass';
  const iMustPass = passing && view.ownPass === null;
  const list = (by: Readonly<Record<BridgeSeat, number>>) => BRIDGE_SEATS.map((s) => `${seatName(s)} ${by[s]}`).join(' · ');

  let status: string;
  if (view.phase === 'done' && result) {
    status = result.moonShooter
      ? result.moonShooter === HUMAN_SEAT
        ? t('hearts-moon-you')
        : t('hearts-moon', { seat: seatName(result.moonShooter) })
      : t('hearts-hand-over', { scores: list(result.taken) });
  } else if (passing) {
    status = iMustPass ? t(`hearts-pass-${view.passDirection as 'left' | 'right' | 'across'}`, { seat: seatName(view.passTo), count: view.passCount }) : t('hearts-pass-waiting');
  } else if (view.canAct) {
    status = view.tricks.length === 0 && view.trick.length === 0 ? t('hearts-turn-lead') : t('hearts-turn-you');
  } else status = t('hearts-turn-wait', { seat: seatName(view.turn) });

  const lastTrick = view.tricks[view.tricks.length - 1];
  const shown = view.trick.length > 0 ? view.trick : (lastTrick?.plays ?? []);
  const winner = view.trick.length === 0 ? lastTrick?.winner : undefined;

  const plate = (seat: BridgeSeat) => {
    const persona = personas[seat];
    const you = seat === HUMAN_SEAT;
    return (
      <SeatPlate
        bubble={bubbles[seat]?.line}
        testid={you ? 'hearts-seat-S' : `hearts-hand-${seat}`}
        name={you ? look.name || seatName(seat) : (persona?.name ?? seatName(seat))}
        tag={you && look.name ? seatName(seat) : you ? undefined : seatName(seat)}
        portrait={you ? null : persona?.portrait}
        fallbackIcon={you ? look.buddy : 'Bot'}
        score={totals[seat]}
        scoreLabel={`${seatName(seat)} ${totals[seat]}`}
        cardsLeft={you ? undefined : view.handSizes[seat]}
        cardsLeftLabel={t('ce-cards-left', { n: view.handSizes[seat] })}
        active={view.phase === 'play' && view.turn === seat}
        you={you}
      />
    );
  };

  const lowest = Math.min(...BRIDGE_SEATS.map((x) => totals[x]));
  const summary: RoundSummary | null = result
    ? {
        round: handNumber,
        outcome: gameWinner
          ? gameWinner === HUMAN_SEAT ? 'win' : 'lose'
          : result.taken[HUMAN_SEAT] <= Math.min(...BRIDGE_SEATS.map((x) => result.taken[x])) ? 'win'
          : result.taken[HUMAN_SEAT] >= Math.max(...BRIDGE_SEATS.map((x) => result.taken[x])) ? 'lose' : 'neutral',
        headline: gameWinner ? (gameWinner === HUMAN_SEAT ? t('hearts-game-won') : t('hearts-game-lost', { seat: seatName(gameWinner) })) : status,
        rows: BRIDGE_SEATS.map((x) => ({
          key: x,
          label: x === HUMAN_SEAT ? look.name || seatName(x) : (personas[x]?.name ?? seatName(x)),
          portrait: x === HUMAN_SEAT ? null : personas[x]?.portrait,
          delta: `+${result.taken[x]}`,
          total: totals[x],
          you: x === HUMAN_SEAT,
          leading: totals[x] === lowest,
        })),
        gameOver: Boolean(gameWinner),
      }
    : null;

  const slot = (seat: BridgeSeat) => {
    const p = shown.find((x) => x.seat === seat);
    return p ? <PlayingCard compact card={p.card} fourColour={fourColour} highlight={winner === seat} /> : <span className={CARD_SLOT_CLASS} aria-hidden="true" />;
  };

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 lg:max-w-6xl lg:gap-6 lg:p-8 xl:max-w-7xl 2xl:max-w-[96rem]" data-testid="hearts-screen" data-appdom-scope="hearts">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold text-berry">{t('hearts-title')}</h1>
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-fg-muted" data-testid="hearts-seed">{t('hearts-deal', { n: handNumber, seed })}</span>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={newGame} data-testid="hearts-new-game" data-appdom-id="hearts-new-game" data-appdom-type="Button">{t('hearts-new-game')}</button>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={onExit} data-testid="hearts-exit" data-appdom-id="hearts-exit" data-appdom-type="Button">{t('bridge-exit')}</button>
        </div>
      </header>

      <output className="min-h-[48px] glass glass-light flex flex-col gap-1 rounded-xl px-3 py-2 text-sm font-bold text-ink" data-testid="hearts-status" data-appdom-id="hearts-status" data-appdom-type="Text" aria-live="polite">
        <span>{status}</span>
        <span className="font-normal text-fg-muted" data-testid="hearts-broken">{view.heartsBroken ? t('hearts-broken') : t('hearts-unbroken')}</span>
      </output>

      <ScoreStrip
        testid="hearts-score"
        title={t('table-score')}
        note={t('hearts-goal', { target })}
        rows={BRIDGE_SEATS.map((x) => ({
          key: x,
          label: x === HUMAN_SEAT ? look.name || seatName(x) : (personas[x]?.name ?? seatName(x)),
          value: totals[x],
          delta: view.pointsTaken[x] > 0 ? `+${view.pointsTaken[x]}` : undefined,
          you: x === HUMAN_SEAT,
          leading: totals[x] === lowest,
        }))}
      />

      {iMustPass && (
        <div className="glass glass-light flex flex-wrap items-center justify-between gap-2 rounded-xl px-3 py-2 max-sm:sticky max-sm:bottom-2 max-sm:z-30 max-sm:bg-surface max-sm:shadow-xl" data-testid="hearts-pass-panel" data-appdom-id="hearts-pass-panel" data-appdom-type="Panel">
          <span className="text-sm font-bold text-ink" data-testid="hearts-pass-count">{t('hearts-pass-chosen', { n: selected.length, count: view.passCount })}</span>
          <button
            type="button"
            onClick={confirmPass}
            disabled={selected.length !== view.passCount}
            data-testid="hearts-pass-confirm"
            className="min-h-[48px] rounded-xl bg-accent px-4 py-2 font-display font-semibold text-accent-fg disabled:opacity-50"
          >
            {t('hearts-pass-confirm', { count: view.passCount })}
          </button>
        </div>
      )}
      {view.phase === 'play' && view.received.length > 0 && view.tricks.length === 0 && (
        <p className="text-center text-sm font-semibold text-fg-muted" data-testid="hearts-received">{t('hearts-received')}</p>
      )}
      {view.phase === 'play' && view.passDirection === 'none' && view.tricks.length === 0 && view.trick.length === 0 && (
        <p className="text-center text-sm font-semibold text-fg-muted" data-testid="hearts-no-pass">{t('hearts-no-pass')}</p>
      )}

      <CardTable
        top={plate('N')}
        left={plate('W')}
        right={plate('E')}
        center={
          <TrickArea
            testid="hearts-trick"
            label={t('bridge-trick-area')}
            north={slot('N')}
            west={slot('W')}
            east={slot('E')}
            south={slot('S')}
          />
        }
        bottom={
          <section aria-label={t('bridge-hand-of', { seat: seatName(HUMAN_SEAT) })} data-testid="hearts-hand-S" className="flex flex-col items-center gap-3">
            {plate(HUMAN_SEAT)}
            <div className={HAND_CLASS}>
              {sortHand(view.hand).map((c) => {
                const id = cardId(c);
                const pickable = iMustPass;
                const playable = !passing && legal.has(id);
                return (
                  <PlayingCard
                    key={id}
                    card={c}
                    fourColour={fourColour}
                    onPlay={pickable ? () => togglePass(c) : playable ? () => play(c) : undefined}
                    highlight={pickable ? chosen.has(id) : received.has(id) && view.tricks.length === 0}
                    dimmed={!pickable && view.canAct && !playable}
                  />
                );
              })}
            </div>
          </section>
        }
      />

      {summary && (
        <RoundSummaryPanel
          testid="hearts-result"
          summary={summary}
          nextLabel={gameWinner ? t('hearts-new-game') : t('hearts-next-hand')}
          onNext={gameWinner ? newGame : nextHand}
          nextTestid={gameWinner ? 'hearts-play-again' : 'hearts-next-hand'}
        />
      )}

      <details className="rounded-2xl border border-border bg-surface p-3" data-testid="hearts-settings">
        <summary className="flex min-h-[48px] cursor-pointer items-center text-sm font-extrabold uppercase tracking-wide text-fg-muted">{t('bridge-robots')}</summary>
        <div className="mt-3 flex flex-col gap-3 text-sm font-bold">
          <div className="flex items-center gap-2">
            {(['easy', 'clever'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={strength === s} data-testid={`hearts-strength-${s}`} onClick={() => setStrength(s)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${strength === s ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                {t(`bridge-${s}`)}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={bigCards} onChange={(e) => setBigCards(e.target.checked)} data-testid="hearts-big-cards" />
            {t('big-cards')}
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={fourColour} onChange={(e) => setFourColour(e.target.checked)} data-testid="hearts-four-colour" />
            {t('bridge-four-colour')}
          </label>
          <p className="font-normal text-fg-muted">{t(bigCards ? 'hearts-teaching-note-big' : 'hearts-teaching-note')}</p>
        </div>
      </details>
    </div>
  );
}
